import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Can, usePermission } from "src/helpers/Auth";
import { extractErrorDetail } from "src/helpers/apiError";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip from "@mui/material/Tooltip";
import { ChunkingStrategy, Dataset } from "src/views/datasets/types";
import { Fragment, useEffect, useRef, useState } from "react";
import TagSelect, { Tag } from "src/reusable/components/select/tag-select";
import {
  useDatasetFields,
  useGetAllDatasetTags,
  useGetDataset,
  useGetDatasetChunkingStrategy,
  useUpdateDataset,
  useUpdateDatasetChunkingStrategy,
  useUpdateDatasetFields,
} from "src/views/datasets/services/DatasetServices";

import Box from "@mui/material/Box";
import { DatasetField } from "src/views/datasets/datasetViewer/DatasetViewer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LoadingButton from "@mui/lab/LoadingButton";
import { ModalBase } from "./ModelBase";
import { NucToast } from "src/reusable/components/Toast";
import { useQueryClient } from "react-query";

// Filter type options for the dropdown
const FILTER_TYPE_OPTIONS = [
  { value: "", label: "Auto (System Default)" },
  { value: "multiSelect", label: "Multi-Select (Checkboxes)" },
  { value: "text", label: "Text Search" },
  { value: "range", label: "Numeric Range" },
  { value: "dateRange", label: "Date Range" },
  { value: "boolean", label: "Yes/No Toggle" },
  { value: "hidden", label: "Hidden (Exclude from Filters)" },
];

type Props = {
  isVisible: boolean;
  onClose: () => void;
  id: number;
  datasetName?: string;
  datasetDescription?: string;
  datasetTags?: Array<string | Tag>;
};

export function UpdateDatasetModal(props: Props) {
  if (!props.isVisible) return null;
  return <UpdateDatasetModalInner {...props} />;
}

function UpdateDatasetModalInner(props: Props) {
  const queryClient = useQueryClient();

  // Mutations
  const updateDataset = useUpdateDataset();
  const updateChuckingStrategy = useUpdateDatasetChunkingStrategy();
  const updateFields = useUpdateDatasetFields() as ReturnType<
    typeof useUpdateDatasetFields
  > & {
    mutateAsync: (vars: {
      datasetId: number;
      fields: { id: number; filter_type_override: string | null }[];
    }) => Promise<unknown>;
  };

  // Querys
  const datasetQuery = useGetDataset(props.id);
  const dataset = datasetQuery.data as Dataset;

  const allChunkingStrategiesQuery = useGetDatasetChunkingStrategy();
  const allChunkingStrategies =
    allChunkingStrategiesQuery.data as Array<ChunkingStrategy>;

  const availableTagsQuery = useGetAllDatasetTags();
  const availableTags = availableTagsQuery.data as Array<Tag>;

  // Fields query for filter type configuration
  const fieldsQuery = useDatasetFields(props.id);
  const fields = (fieldsQuery.data ?? []) as DatasetField[];

  const permissions = usePermission("search:update");
  const canChangeChunkingPermission = permissions.canIt;

  // Local state for field filter type changes
  const [fieldChanges, setFieldChanges] = useState<
    Record<number, string | null>
  >({});

  // Hooks
  const datasetCloneRef = useRef<Dataset | null>(null);
  if (dataset && !datasetCloneRef.current) {
    datasetCloneRef.current = structuredClone(dataset);
  }

  // Handler for field filter type changes
  const handleFieldFilterTypeChange = (fieldId: number, value: string) => {
    setFieldChanges((prev) => ({
      ...prev,
      [fieldId]: value === "" ? null : value,
    }));
  };

  // Handlers
  const onClose = () => {
    props.onClose();
    queryClient.invalidateQueries("datasetList");
    queryClient.invalidateQueries(["dataset", props.id]);
  };

  const submitHandler = async () => {
    // Save field filter type changes if any
    const changedFieldIds = Object.keys(fieldChanges);
    if (changedFieldIds.length > 0) {
      const fieldsToUpdate = changedFieldIds.map((id) => ({
        id: parseInt(id),
        filter_type_override: fieldChanges[parseInt(id)],
      }));
      try {
        await updateFields.mutateAsync({
          datasetId: props.id,
          fields: fieldsToUpdate,
        });
      } catch (error) {
        NucToast({
          message: "Failed to update field settings",
          type: "error",
        });
        return;
      }
    }

    if (
      dataset?.chunking_strategy_id &&
      dataset.chunking_strategy_id !==
        datasetCloneRef.current?.chunking_strategy_id
    ) {
      // @ts-expect-error: React Query is expecting void rather than a tuple of numbers
      updateChuckingStrategy.mutate([props.id, dataset.chunking_strategy_id]);
    }

    updateDataset.mutate({
      id: props.id,
      description: dataset.description,
      tags: dataset.tags,
      tool_updatable: dataset.tool_updatable,
      include_image_sources: dataset.include_image_sources ?? false,
    });
  };

  const updateAvailableTags = (tags: Array<Tag>) => {
    queryClient.setQueryData(["allDatasetTags"], () => tags);
  };

  const onChangeHandler = (
    eventName: keyof Dataset,
    eventValue: string | number | Tag[] | Date | boolean,
  ) => {
    queryClient.setQueryData<Dataset>(["dataset", props.id], (prev) => {
      if (prev) {
        return { ...prev, [eventName]: eventValue };
      } else {
        return dataset;
      }
    });
  };

  useEffect(() => {
    if (updateChuckingStrategy.isSuccess || updateDataset.isSuccess) {
      NucToast({
        message: "Update successful!",
        type: "success",
      });
      onClose();
    }
  }, [
    updateChuckingStrategy.isError,
    updateDataset.isError,
    updateChuckingStrategy.isSuccess,
    updateDataset.isSuccess,
  ]);

  return (
    <ModalBase
      title="Update Dataset"
      isVisible={props.isVisible}
      onClose={onClose}
    >
      <Fragment>
        {dataset ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {(updateDataset.isError || updateChuckingStrategy.isError) && (
              <Alert severity="error">
                {extractErrorDetail(
                  updateDataset.error ?? updateChuckingStrategy.error,
                  "Something went wrong!",
                )}
              </Alert>
            )}
            <Box
              sx={{ background: "#33333310", p: "12px", borderRadius: "8px" }}
            >
              <Typography sx={{ opacity: 0.7 }}> Dataset </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {dataset.name}
              </Typography>
            </Box>

            <TextField
              multiline
              minRows={3}
              maxRows={3}
              value={dataset.description}
              onChange={(e) => onChangeHandler("description", e.target.value)}
              label="Description"
              name="description"
              id="description-input"
              data-testid="ds-modal-update-input-desc"
              variant="outlined"
              fullWidth
              placeholder="Enter a description if needed"
            />
            <TagSelect
              value={dataset.tags ?? []}
              onChange={(tags) => {
                onChangeHandler("tags", tags);
              }}
              availableTags={availableTags}
              setAvailableTags={(tags) => {
                onChangeHandler("tags", [...(dataset.tags || []), tags[0]]);
                updateAvailableTags(tags);
              }}
              placeholder="Tags for model"
              allowCreation={true}
              isSmText={true}
            />
            {/* Hide chunking strategy selector for tabular datasets (types 1, 4, 5)
                since they can only use 'Chunk Entire Source Data Record Fields' */}
            {![1, 4, 5].includes(dataset?.dataset_type_id ?? 0) && (
              <FormControl>
                <InputLabel>Chunking Strategy</InputLabel>
                <Select
                  value={dataset.chunking_strategy_id}
                  onChange={(e) =>
                    onChangeHandler("chunking_strategy_id", e.target.value)
                  }
                  name="customChunkingStratagy"
                  label="Chunking Strateg"
                  disabled={!canChangeChunkingPermission}
                >
                  {allChunkingStrategies?.map((item) => (
                    <MenuItem
                      key={item.id}
                      sx={{ textTransform: "capitalize" }}
                      value={item.id}
                    >
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={dataset.tool_updatable ?? false}
                  onChange={(_, check) =>
                    onChangeHandler("tool_updatable", check)
                  }
                  data-testid="tool-updatable-checkbox"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle1" fontWeight="semibold">
                    Tool Updatable
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Allow tools to programmatically update this dataset
                  </Typography>
                </Box>
              }
            />
            <Can
              perform="dataset:write"
              product="epic-ocr"
              yes={() => (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dataset.include_image_sources ?? false}
                        onChange={(_, check) =>
                          onChangeHandler("include_image_sources", check)
                        }
                        data-testid="include-image-sources-checkbox"
                      />
                    }
                    label={
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="semibold">
                            Include Image Sources
                          </Typography>
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  gutterBottom
                                >
                                  What are image sources?
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  When enabled, images extracted from your
                                  documents (charts, diagrams, figures) are
                                  analyzed with vision-language models and
                                  indexed as searchable sources alongside text.
                                </Typography>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  gutterBottom
                                >
                                  Key Features:
                                </Typography>
                                <Typography variant="body2" component="div">
                                  • Image analysis for charts, diagrams, and
                                  figures
                                  <br />
                                  • Extracted images appear as sources in chat
                                  <br />• Citations link back to the original
                                  image in the document
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="right"
                          >
                            <IconButton
                              size="small"
                              aria-label="Image sources help"
                              sx={{ p: 0 }}
                            >
                              <HelpOutlineIcon
                                sx={{
                                  fontSize: 18,
                                  color: "text.secondary",
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Index extracted images as searchable sources in chat
                        </Typography>
                      </Box>
                    }
                  />
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Image sources are only created for newly uploaded documents.
                    Retroactive processing for existing documents is coming
                    soon.
                  </Alert>
                </Box>
              )}
            />

            {/* Field Filter Settings - Collapsible */}
            <Accordion
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                "&:before": { display: "none" },
                boxShadow: "none",
              }}
              data-testid="field-filter-settings-accordion"
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={500}>Field Filter Settings</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  ({fields.filter((f) => f.is_visible).length} fields)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Customize how each field appears in filters. Filter types are
                  auto-classified by AI on first load.
                </Typography>
                {fieldsQuery.isLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Field Name</TableCell>
                          <TableCell sx={{ width: 220 }}>Filter Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fields
                          .filter((f) => f.is_visible)
                          .sort((a, b) =>
                            a.field_name.localeCompare(b.field_name),
                          )
                          .map((field) => (
                            <TableRow key={field.id}>
                              <TableCell>
                                <Typography variant="body2">
                                  {field.field_name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Select
                                  size="small"
                                  fullWidth
                                  value={
                                    fieldChanges[field.id] !== undefined
                                      ? (fieldChanges[field.id] ?? "")
                                      : (field.filter_type_override ?? "")
                                  }
                                  onChange={(e) =>
                                    handleFieldFilterTypeChange(
                                      field.id,
                                      e.target.value as string,
                                    )
                                  }
                                  data-testid={`filter-type-select-${field.id}`}
                                >
                                  {FILTER_TYPE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>

            <LoadingButton
              onClick={submitHandler}
              sx={{ width: "fit-content", ml: "auto" }}
              variant="contained"
              data-testid="ds-modal-update-btn-submit"
              disabled={
                updateChuckingStrategy.isLoading || updateFields.isLoading
              }
              loading={
                updateDataset.isLoading ||
                updateChuckingStrategy.isLoading ||
                updateFields.isLoading
              }
            >
              Update Dataset
            </LoadingButton>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "60px",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Fragment>
    </ModalBase>
  );
}
